import useQuery from '@/api/queries/useQuery';
import { UseQueryResult } from '@tanstack/react-query';

import { usePartnerScoreGlobalFilter } from '@/components/partner';
import { verifyRequestedPeriodHasData } from '@/components/partner/utils/partnerScoreUtils';
import { usePartnerScoreFilter, UsePartnerScoreFilterReturn } from '@/pages/PartnerScore/components/PartnerScoreFilter';
import { CubeQuery } from '@/types/cube';
import { toNumberOrNull } from '@/utils/cube/mapper';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { PARTNERSCORE_CUBE_NAME } from '@api/hcp';
import { AxiosError } from 'axios';
import { PartnerScoreCacheKeys } from '../../cacheKeys';
import { sortByYearQuarter } from '../../utils';

export type UseGetAverageCesScoreResponse = {
  currentAverage: number;
  change: number;
};

interface UseGetAverageCesScoreParams extends Pick<UsePartnerScoreFilterReturn, 'year' | 'quarter'> {
  surveyIdList?: UsePartnerScoreFilterReturn['surveyIdList'];
  country?: string;
}

type PeriodRecordType = {
  year: number;
  quarter: number;
  total: number;
  positiveAnswers: number;
  otherAnswers: number;
};

function useGetAverageCesScore<TData = UseGetAverageCesScoreResponse, TError = AxiosError>({
  year,
  quarter,
  surveyIdList,
  country: hcpCountry,
}: UseGetAverageCesScoreParams): UseQueryResult<TData, TError> {
  const { globalFiltersWithoutHcpCountryCode, countries } = usePartnerScoreGlobalFilter();
  const { filtersWithImmediatePreviousPeriods, filterQuarterDimensions, filterSurveyList } = usePartnerScoreFilter();

  const selectedYear = year ?? 0;
  const selectedQuarter = quarter ?? 0;

  const periodFilters =
    filtersWithImmediatePreviousPeriods?.({
      cubeNameArg: PARTNERSCORE_CUBE_NAME,
      currentYear: selectedYear,
      currentQuarter: selectedQuarter,
    }) ?? [];

  const baseDimensions = [
    `${PARTNERSCORE_CUBE_NAME}.quarter`,
    `${PARTNERSCORE_CUBE_NAME}.year`,
    `${PARTNERSCORE_CUBE_NAME}.trans_ces_q16_answer`,
  ];

  const dimensions = filterQuarterDimensions?.(baseDimensions, selectedQuarter) ?? baseDimensions;

  const query: CubeQuery = {
    measures: [`${PARTNERSCORE_CUBE_NAME}.countCesScoreResponses`],
    dimensions,
    filters: [
      ...periodFilters,
      {
        member: `${PARTNERSCORE_CUBE_NAME}.country_code`,
        operator: 'equals',
        values: hcpCountry ? [hcpCountry] : countries,
      },
      {
        member: `${PARTNERSCORE_CUBE_NAME}.trans_ces_q16_answer`,
        operator: 'set',
      },
      ...(filterSurveyList?.(PARTNERSCORE_CUBE_NAME, surveyIdList ?? []) ?? []),
      globalFiltersWithoutHcpCountryCode,
    ],
    order: { [`${PARTNERSCORE_CUBE_NAME}.year`]: 'desc', [`${PARTNERSCORE_CUBE_NAME}.quarter`]: 'desc' },
    limit: 100,
  };

  const isEnabled = (!!countries.length || !!hcpCountry) && !!year && !!quarter;

  return useQuery<CubeRawRow[], TError, TData>({
    payload: query,
    options: {
      queryKey: [...PartnerScoreCacheKeys.cesAverage(query)],
      select: (data): TData => {
        if (!data.length || !verifyRequestedPeriodHasData(data, year)) {
          return { change: null, currentAverage: null } as TData;
        }

        const sortedData = sortByYearQuarter(
          data.map(dataItem => ({
            year: toNumberOrNull(dataItem[`${PARTNERSCORE_CUBE_NAME}.year`]) ?? 0,
            quarter: toNumberOrNull(dataItem[`${PARTNERSCORE_CUBE_NAME}.quarter`]) ?? 0,
            countCesScoreResponses: toNumberOrNull(dataItem[`${PARTNERSCORE_CUBE_NAME}.countCesScoreResponses`]) ?? 0,
            score: toNumberOrNull(dataItem[`${PARTNERSCORE_CUBE_NAME}.trans_ces_q16_answer`]) ?? 0,
          })),
          selectedYear,
          selectedQuarter,
        );

        const periodRecord = new Map<string, PeriodRecordType>();

        sortedData.forEach(sortedDataItem => {
          const period = `${sortedDataItem.year} ${sortedDataItem.quarter}`;
          const existingPeriod = periodRecord.get(period);

          if (existingPeriod) {
            if (sortedDataItem.score === 4 || sortedDataItem.score === 5) {
              existingPeriod.positiveAnswers += sortedDataItem.countCesScoreResponses;
            }
            if (sortedDataItem.score < 4) {
              existingPeriod.otherAnswers += sortedDataItem.countCesScoreResponses;
            }
            existingPeriod.total += sortedDataItem.countCesScoreResponses;
          } else {
            periodRecord.set(period, {
              year: sortedDataItem.year,
              quarter: sortedDataItem.quarter,
              total: sortedDataItem.countCesScoreResponses,
              positiveAnswers:
                sortedDataItem.score === 4 || sortedDataItem.score === 5 ? sortedDataItem.countCesScoreResponses : 0,
              otherAnswers: sortedDataItem.score < 4 ? sortedDataItem.countCesScoreResponses : 0,
            });
          }
        });

        const [current, previous] = Array.from(periodRecord.values());

        const calculateAverage = (record: PeriodRecordType) => {
          if (!record) {
            return null;
          }
          return +((record.positiveAnswers / record.total) * 100).toFixed();
        };

        const currentAverage = calculateAverage(current);
        const previousAverage = calculateAverage(previous);
        const change = previousAverage && currentAverage !== null ? currentAverage - previousAverage : null;

        return {
          currentAverage,
          change,
        } as TData;
      },
      enabled: isEnabled,
    },
  });
}

export default useGetAverageCesScore;
